import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { EntityForm } from '@components/integrated/entity-form';
import type { FieldConfig } from '@components/integrated/entity-form';
import { Text } from '@components/base/display';
import { VStack } from '@components/base/layout';
import { Breadcrumb } from '@components/base/navigation';
import { Spinner, Alert } from '@components/base/feedback';
import { useAdminFormPage } from '../../hooks/useAdminFormPage';
import { getWorkflowStepFormConfig } from '../../configs/workflowStepFormConfig';
import { workflowStepsService } from '../../api/workflowStepsService';
import { parseJsonField } from '../../utils/parseJsonField';
import { StepConfigPanel } from './step-config/StepConfigPanel';
import { serializeConfig, deserializeConfig, getStepTypeLabel } from './step-config/types';
import type { StoreWorkflowStepRequest, UpdateWorkflowStepRequest } from '../../api/workflowStepsService';

interface WorkflowStepParams {
  slug?: string;
  stepId?: string;
}

const NON_CONFIG_JSON_FIELDS = ['condition', 'retry_policy'] as const;

export function WorkflowStepFormPage() {
  const formPage = useAdminFormPage<unknown, WorkflowStepParams>({
    entityName: 'workflow step',
    getConfig: (mode, params) =>
      getWorkflowStepFormConfig(mode, params.slug!, params.stepId),
    getEditId: (params) => params.stepId,
    queryKey: (params) => ['admin', 'workflows', params.slug, 'steps', params.stepId],
    fetchFn: (params) => workflowStepsService.get(params.slug!, params.stepId!),
    fetchEnabled: (isEdit, params) => isEdit && !!params.slug,
    mapToInitialValues: (entity) => {
      const step = entity as Record<string, unknown>;
      const stepType = step.step_type as string | undefined;
      const configObj = (step.config as Record<string, unknown>) ?? {};

      const configValues = stepType
        ? deserializeConfig(stepType, configObj)
        : {};

      return {
        name: step.name,
        step_type: step.step_type,
        sort_order: step.sort_order,
        execution_mode: (step.execution_mode as string) ?? 'sync',
        is_active: step.is_active,
        timeout_seconds: (step.timeout_seconds as number | null) ?? '',
        condition: step.condition ? JSON.stringify(step.condition, null, 2) : '{}',
        retry_policy: step.retry_policy ? JSON.stringify(step.retry_policy, null, 2) : '{}',
        ...configValues,
        config_iterate_group: configValues.config_iterate_group ?? '',
        config_iterate_over: configValues.config_iterate_over ?? '',
        config_iterate_as: configValues.config_iterate_as ?? '',
      };
    },
    mutationFn: async (payload, isEdit, params) => {
      const cleanedPayload: Record<string, unknown> = {};

      for (const [key, value] of Object.entries(payload)) {
        if (!key.startsWith('config_')) {
          cleanedPayload[key] = value;
        }
      }

      const stepType = payload.step_type as string | undefined;
      if (stepType) {
        cleanedPayload.config = serializeConfig(stepType, payload);
      } else {
        cleanedPayload.config = {};
      }

      for (const field of NON_CONFIG_JSON_FIELDS) {
        cleanedPayload[field] = parseJsonField(payload[field]);
      }

      cleanedPayload.timeout_seconds = payload.timeout_seconds
        ? Number(payload.timeout_seconds)
        : null;

      if (isEdit && params.slug && params.stepId) {
        return workflowStepsService.update(
          params.slug,
          params.stepId,
          cleanedPayload as UpdateWorkflowStepRequest,
        );
      }
      return workflowStepsService.create(
        params.slug!,
        cleanedPayload as unknown as StoreWorkflowStepRequest,
      );
    },
    invalidateKey: (params) => ['admin', 'workflows', params.slug, 'steps'],
    navigateBackPath: (params) => `/admin/workflows/${params.slug}`,
  });

  const {
    isEdit,
    config,
    isFetchLoading,
    fetchError,
    submitError,
    setSubmitError,
    patchedForm,
  } = formPage;

  // ---- Fetch step types dynamically ----------------------------------------

  const { data: stepTypesResponse } = useQuery({
    queryKey: ['admin', 'workflows', 'step-types'],
    queryFn: () => workflowStepsService.getStepTypes(),
    staleTime: 5 * 60 * 1000,
  });
  const stepTypes = stepTypesResponse?.data;

  const derivedConfig = useMemo((): typeof config => {
    if (!stepTypes) return config;
    return {
      ...config,
      fields: {
        ...config.fields,
        step_type: {
          ...config.fields.step_type,
          options: {
            items: stepTypes.map((t) => ({ value: t, label: getStepTypeLabel(t) })),
          },
        } as FieldConfig,
      },
    };
  }, [config, stepTypes]);

  // ---- Breadcrumbs ---------------------------------------------------------

  const breadcrumbs = useMemo(() => [
    { label: 'Admin', href: '/admin' },
    { label: 'Workflows', href: '/admin/workflows' },
    { label: formPage.params.slug ?? '', href: `/admin/workflows/${formPage.params.slug}` },
    { label: isEdit ? 'Edit Step' : 'Create Step' },
  ], [isEdit, formPage.params.slug]);

  // ---- Loading state -------------------------------------------------------

  if (isEdit && isFetchLoading) {
    return (
      <VStack gap={6} align="center" className="py-12">
        <Spinner size="lg" />
        <Text color="muted">Loading step...</Text>
      </VStack>
    );
  }

  // ---- Fetch error ---------------------------------------------------------

  if (isEdit && fetchError) {
    return (
      <VStack gap={6}>
        <Breadcrumb>
          {breadcrumbs.map((item, index) => (
            <Breadcrumb.Item
              key={item.label}
              href={index < breadcrumbs.length - 1 ? item.href : undefined}
              isCurrent={index === breadcrumbs.length - 1}
            >
              {item.label}
            </Breadcrumb.Item>
          ))}
        </Breadcrumb>
        <Alert variant="error" title="Failed to load step">
          {fetchError instanceof Error
            ? fetchError.message
            : 'An unexpected error occurred while loading the workflow step.'}
        </Alert>
      </VStack>
    );
  }

  // ---- Main render ---------------------------------------------------------

  const currentStepType = (patchedForm.getValue('step_type') as string) ?? '';

  return (
    <VStack gap={6}>
      <Breadcrumb>
        {breadcrumbs.map((item, index) => (
          <Breadcrumb.Item
            key={item.label}
            href={index < breadcrumbs.length - 1 ? item.href : undefined}
            isCurrent={index === breadcrumbs.length - 1}
          >
            {item.label}
          </Breadcrumb.Item>
        ))}
      </Breadcrumb>

      <Text as="h1" size="2xl" weight="bold">
        {isEdit ? 'Edit Step' : 'Create Step'}
      </Text>

      {submitError && (
        <Alert
          variant="error"
          title="Save failed"
          dismissible
          onDismiss={() => { setSubmitError(null); }}
        >
          {submitError}
        </Alert>
      )}

      <EntityForm config={derivedConfig} form={patchedForm} className="space-y-6">
        <EntityForm.Alert />
        <EntityForm.Fields />
        <StepConfigPanel
          stepType={currentStepType}
          getValue={patchedForm.getValue}
          setValue={patchedForm.setValue}
        />
        <EntityForm.Actions />
      </EntityForm>
    </VStack>
  );
}
